/**
 * @file `SecretRow` + `RotateModal` — the write-only Secrets sub-view
 *       rows (umbrella #883, part #894).
 * @module @labcraft/admin/ui/settings/secret-row
 *
 * A secret's value never crosses the wire: the row shows only a mask,
 * the version, and the last-rotated timestamp (or "never rotated").
 * Rotation goes through a modal whose copy is explicit that the entered
 * value is never displayed back — it is written straight to Bao.
 */

import { useCallback, useState, type JSX } from 'react'
import { useRotateSecretMutation } from '../hooks/index.ts'
import type { SecretEntry } from './view.ts'
import { SettingsView } from './view.ts'

/** Props for {@link SecretRow}. */
export type SecretRowProps = {
  entry: SecretEntry
  now: number
}

/** Render one declared-secret row. */
export function SecretRow({ entry, now }: SecretRowProps): JSX.Element {
  const [rotating, setRotating] = useState(false)
  const stale = SettingsView.isStaleSecret(entry, now)
  const { definition, version, lastRotated } = entry

  return (
    <li className="cfg-row">
      <div className="cfg-row__main">
        <span className="cfg-row__label">{definition.label}</span>
        <p className="cfg-row__desc">{definition.description}</p>
        <p className="cfg-row__meta">
          <span className="cfg-row__key">{definition.path}</span>
          <span>{definition.daemon}</span>
        </p>
      </div>
      <div className="cfg-row__control">
        <div className="cfg-row__widget secret-state">
          <span className="secret-mask">••••••••</span>
          {version !== null ? (
            <span className="secret-ver">v{version}</span>
          ) : (
            <span className="secret-never">never rotated</span>
          )}
          {lastRotated !== null && (
            <span
              className={`secret-when${stale ? ' secret-when--stale' : ''}`}>
              {formatDate(lastRotated)}
            </span>
          )}
        </div>
        <div className="cfg-row__actions">
          <button
            type="button"
            className="lc-btn lc-btn--ghost"
            onClick={() => setRotating(true)}>
            Rotate
          </button>
        </div>
      </div>
      {rotating && (
        <RotateModal entry={entry} onClose={() => setRotating(false)} />
      )}
    </li>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Rotate modal
////////////////////////////////////////////////////////////////////////////////

/** Write-only rotate form — the value is never read back. */
function RotateModal(props: {
  entry: SecretEntry
  onClose: () => void
}): JSX.Element {
  const [value, setValue] = useState('')
  const rotate = useRotateSecretMutation()
  const path = props.entry.definition.path

  const submit = useCallback(() => {
    if (value.trim().length === 0) return
    rotate.mutate({ path, value }, { onSuccess: () => props.onClose() })
  }, [path, value, rotate, props])

  const error = rotate.isError
    ? rotate.error.message || 'Rotation failed.'
    : null

  return (
    <div className="lc-modal-backdrop" onClick={props.onClose}>
      <div className="lc-modal" onClick={e => e.stopPropagation()}>
        <div className="lc-modal__head">
          <div>
            <h2 className="lc-modal__title">
              Rotate {props.entry.definition.label}
            </h2>
            <div className="lc-modal__sub">{path}</div>
          </div>
          <button
            className="lc-modal__close"
            onClick={props.onClose}
            aria-label="Close">
            ✕
          </button>
        </div>
        <div className="lc-modal__body">
          <div className="field">
            <label className="field__label" htmlFor="secret-value">
              New value
            </label>
            <input
              id="secret-value"
              type="password"
              className="lc-input"
              value={value}
              autoFocus
              autoComplete="off"
              onChange={e => setValue(e.target.value)}
            />
          </div>
          <div className="field__warn">
            // the value is written straight to Bao — it is never displayed
            here, returned on the wire, or written to logs. There is no "show"
            affordance by design.
          </div>
          {error !== null && (
            <div className="cfg-row__error" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}
        </div>
        <div className="lc-modal__foot">
          <button className="lc-btn lc-btn--ghost" onClick={props.onClose}>
            Cancel
          </button>
          <button
            className="lc-btn lc-btn--primary"
            disabled={value.trim().length === 0 || rotate.isPending}
            onClick={submit}>
            {rotate.isPending ? 'Rotating…' : 'Rotate'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** `2026-05-21T12:00:00Z` → `May 21, 2026`. */
function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
